import { deployEnvironment, ENVIRONMENT_LABEL } from '@features/clients';
import { Button, FlagKeyPill, Modal, WriteError } from '@shared/ui';
import { useState } from 'react';

import { useStartAllOperation } from '../hooks/use-release-operation';

import { confirmView } from './operation-confirm';
import { explainReleaseWriteFailure } from './release-error-copy';
import { TurnOffConfirmFields } from './TurnOffConfirmFields';
import { matchesPhrase, turnOffAlert } from './typed-phrase';

import type { ReleaseComponent } from '../schemas/release-component';

/**
 * Turn a release off for all clients (engineer), behind the typed phrase
 * (ACR-1598). Mounted only while open. Answers 202; the page then shows status.
 */
export function TurnOffReleaseDialog({
    release,
    onClose,
}: {
    release: ReleaseComponent;
    onClose: () => void;
}) {
    const start = useStartAllOperation(release.id);
    const [text, setText] = useState('');
    const failure = start.error ? explainReleaseWriteFailure(start.error) : null;
    const members = release.members ?? [];
    const view = confirmView({
        releaseName: release.name,
        enabled: false,
        flags: members.map((member) => ({
            key: member.key,
            name: member.name,
            archived: member.archived,
        })),
        environment: ENVIRONMENT_LABEL[deployEnvironment()],
    });
    const alert = turnOffAlert({
        scope: 'all',
        flagCount: members.length,
        salesforceFlags: members.filter((member) => member.type === 'salesforce'),
    });

    const submit = () => {
        if (!matchesPhrase(text)) return;
        start.mutate({ enabled: false }, { onSuccess: onClose });
    };

    return (
        <Modal
            open
            onClose={onClose}
            dismissable={!start.isPending}
            title={view.title}
            description={view.overrideNotice}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={start.isPending}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={submit}
                        disabled={start.isPending || !matchesPhrase(text)}
                    >
                        {start.isPending ? 'Starting…' : view.confirmLabel}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-3 text-[13px]">
                <p className="font-medium text-content-strong">{view.environmentLabel}</p>
                <div>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                        {view.summary}
                    </p>
                    <ul className="flex flex-col gap-1.5">
                        {view.flags.map((flag) => (
                            <li key={flag.key} className="flex items-center gap-2">
                                <FlagKeyPill>{flag.key}</FlagKeyPill>
                                {flag.note !== undefined && (
                                    <span className="text-xs text-content-muted">{flag.note}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
                <TurnOffConfirmFields
                    sentence={alert.sentence}
                    salesforceNotes={alert.salesforceNotes}
                    text={text}
                    onChange={setText}
                    disabled={start.isPending}
                />
                {failure ? (
                    <div
                        role="alert"
                        className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-danger-text"
                    >
                        <p className="font-semibold">{failure.title}</p>
                        <p className="mt-0.5 text-danger-muted">{failure.body}</p>
                        {failure.detail !== undefined && (
                            <p className="mt-1.5 text-danger-muted">{failure.detail}</p>
                        )}
                    </div>
                ) : (
                    start.error && <WriteError error={start.error} />
                )}
            </div>
        </Modal>
    );
}
